'use client';

import React from 'react';
import { Layers, ChevronLeft, ChevronRight } from 'lucide-react';
import { WorkspaceItem } from '../types';

interface WorkspacesTabProps {
  workspaces: WorkspaceItem[];
  wsTotal: number;
  wsPage: number;
  setWsPage: React.Dispatch<React.SetStateAction<number>>;
  wsTotalPages: number;
  pageSize: number;
  loading: boolean;
  searchTerm: string;
  handleOpenWorkspaceBoards: (wsId: string) => void;
  setSelectedWorkspaceId: (id: string) => void;
  setSelectedWorkspaceName: (name: string) => void;
  setActiveTab: (tab: 'OVERVIEW' | 'KYC' | 'WORKSPACES' | 'USERS' | 'AUDIT_LOG') => void;
}

export const WorkspacesTab: React.FC<WorkspacesTabProps> = ({
  workspaces,
  wsTotal,
  wsPage,
  setWsPage,
  wsTotalPages,
  pageSize,
  loading,
  searchTerm,
  handleOpenWorkspaceBoards,
  setSelectedWorkspaceId,
  setSelectedWorkspaceName,
  setActiveTab,
}) => {
  return (
    <div className="bg-[#0A0D1A] border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-400" />
            <span>Global Workspaces Directory</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1">Click on any workspace's Board Count to inspect all of its boards and task cards.</p>
        </div>

        <div className="text-xs font-mono text-slate-400 font-semibold bg-[#060811] border border-slate-800 px-3 py-1.5 rounded-xl">
          Total Results: <strong className="text-indigo-400">{wsTotal}</strong>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 uppercase text-[10px] tracking-wider">
              <th className="py-3.5 px-4 font-bold">Workspace Name</th>
              <th className="py-3.5 px-4 font-bold">Slug Identifier</th>
              <th className="py-3.5 px-4 font-bold">Total Members</th>
              <th className="py-3.5 px-4 font-bold">Total Boards (Click to Inspect)</th>
              <th className="py-3.5 px-4 font-bold">Created Date</th>
              <th className="py-3.5 px-4 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-400 font-medium">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    <span>Fetching workspaces from backend database...</span>
                  </div>
                </td>
              </tr>
            ) : workspaces.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-500 font-medium">
                  No matching workspaces found for "{searchTerm}".
                </td>
              </tr>
            ) : (
              workspaces.map((ws) => (
                <tr
                  key={ws.id}
                  onClick={() => handleOpenWorkspaceBoards(ws.id)}
                  className="hover:bg-[#060811] transition-colors cursor-pointer group"
                >
                  <td className="py-4 px-4 font-bold text-white group-hover:text-indigo-300 transition-colors">{ws.name}</td>
                  <td className="py-4 px-4 font-mono text-indigo-400">/{ws.slug}</td>
                  <td className="py-4 px-4 font-semibold text-slate-300">{ws.memberCount} members</td>
                  <td className="py-4 px-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenWorkspaceBoards(ws.id);
                      }}
                      className="px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/25 border border-purple-500/30 text-purple-300 font-extrabold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm hover:scale-105"
                      title="Click to view all boards of this workspace"
                    >
                      <Layers className="w-3.5 h-3.5 text-purple-400" />
                      <span>{ws.boardCount} Boards</span>
                    </button>
                  </td>
                  <td className="py-4 px-4 text-slate-400">
                    {new Date(ws.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedWorkspaceId(ws.id);
                        setSelectedWorkspaceName(ws.name);
                        setActiveTab('AUDIT_LOG');
                      }}
                      className="px-2.5 py-1.5 bg-indigo-600/10 hover:bg-indigo-650 text-indigo-400 hover:text-white rounded-xl border border-indigo-500/20 text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
                    >
                      Audit Log
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Workspaces Backend Pagination Bar */}
      {wsTotalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-800/80">
          <div className="text-xs text-slate-400 font-medium">
            Showing <strong className="text-white">{(wsPage - 1) * pageSize + 1}</strong> to{' '}
            <strong className="text-white">{Math.min(wsPage * pageSize, wsTotal)}</strong> of{' '}
            <strong className="text-white">{wsTotal}</strong> workspaces
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setWsPage((p) => Math.max(p - 1, 1))}
              disabled={wsPage === 1}
              className="px-3.5 py-1.5 bg-[#060811] hover:bg-slate-800 disabled:opacity-40 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 flex items-center gap-1 transition-all cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <span className="text-xs font-mono font-bold text-slate-300 px-3">
              Page {wsPage} of {wsTotalPages}
            </span>

            <button
              onClick={() => setWsPage((p) => Math.min(p + 1, wsTotalPages))}
              disabled={wsPage >= wsTotalPages}
              className="px-3.5 py-1.5 bg-[#060811] hover:bg-slate-800 disabled:opacity-40 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 flex items-center gap-1 transition-all cursor-pointer"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
